import EventPosters from "./admin/EventPosters";
import CertificateManager from "./admin/CertificateManager";
import NotificationBell from "../components/NotificationBell";
import ViewPosters from "./ViewPosters";
import MemberDirectory from "./MemberDirectory";
import { ImageIcon } from "lucide-react";
import { Award } from "lucide-react";
import { BookUser } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import trustLogo from "../assets/trust_logo.png";
import Overview from "./Overview";
import Profile from "./Profile";
import Attendance from "./Attendance";
import Donations from "./Donations";
import Documents from "./Documents";
import Members from "./admin/Members";
import AddMember from "./admin/AddMember";
import MarkAttendance from "./admin/MarkAttendance";
import Campaigns from "./Campaigns";
import RegistrationManager from "./admin/RegistrationManager";
import CampaignManager from "./admin/CampaignManager";
import { LayoutDashboard, User, CalendarCheck, HeartHandshake, FileText, Users, UserPlus, ClipboardList, LogOut, Menu, X, ChevronRight, Bell, Shield, UserCheck } from "lucide-react";

const NAV = [
  { id:"overview",   label:"Overview",          icon:LayoutDashboard },
  { id:"profile",    label:"My Profile",        icon:User },
  { id:"attendance", label:"Attendance",        icon:CalendarCheck },
  { id:"donations",  label:"Donations",         icon:HeartHandshake },
  { id:"documents",  label:"Documents",         icon:FileText },
  { id:"directory",  label:"Our Members",       icon:BookUser },
  { id:"posters",    label:"Events & Updates",  icon:ImageIcon },
  { id:"campaigns", label:"Campaigns", icon:HeartHandshake },
];
const ADMIN_NAV = [
  { id:"members",          label:"All Members",     icon:Users },
  { id:"add-member",       label:"Add Member",      icon:UserPlus },
  { id:"mark-attendance",  label:"Mark Attendance", icon:ClipboardList },
  { id:"event-posters",    label:"Event Posters",   icon:ImageIcon },
  { id:"certificates",     label:"Certificates",    icon:Award },
  { id:"campaign-manager", label:"Manage Campaigns", icon:HeartHandshake },
   { id:"registrations", label:"Registrations", icon:UserCheck }

];

const ROLE_COLORS = {
  superadmin:{ bg:"rgba(245,158,11,0.15)", color:"#f59e0b", border:"rgba(245,158,11,0.3)" },
  admin:     { bg:"rgba(59,130,246,0.15)", color:"#3b82f6", border:"rgba(59,130,246,0.3)" },
  member:    { bg:"rgba(34,197,94,0.15)",  color:"#22c55e", border:"rgba(34,197,94,0.3)"  },
  volunteer: { bg:"rgba(168,85,247,0.15)", color:"#a855f7", border:"rgba(168,85,247,0.3)" },
  intern:    { bg:"rgba(236,72,153,0.15)", color:"#ec4899", border:"rgba(236,72,153,0.3)" },
};

export default function Dashboard() {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState("overview");
  const [menuOpen, setMenuOpen] = useState(false);

  const role = userProfile?.role || "member";
  const name = userProfile?.name || currentUser?.email?.split("@")[0] || "User";
  const isAdmin = role === "admin" || role === "superadmin";
  const isSuperOrAdmin = role === "admin" || role === "superadmin";
  const rc = ROLE_COLORS[role] || ROLE_COLORS.member;

  async function handleLogout() {
    try { await logout(); navigate("/login"); toast.success("Signed out!"); }
    catch { toast.error("Failed to sign out"); }
  }

  function goTo(p) { setPage(p); setMenuOpen(false); window.scrollTo({top:0}); }

  const renderPage = () => {
    switch(page) {
      case "overview":         return <Overview goTo={goTo}/>;
      case "profile":          return <Profile/>;
      case "attendance":       return <Attendance/>;
      case "donations":        return <Donations/>;
      case "documents":        return <Documents/>;
      case "members":          return isAdmin ? <Members/> : <Overview goTo={goTo}/>;
      case "add-member":       return isAdmin ? <AddMember/> : <Overview goTo={goTo}/>;
      case "mark-attendance":  return isAdmin ? <MarkAttendance/> : <Overview goTo={goTo}/>;
      case "directory": return <MemberDirectory/>;
      case "event-posters": return <EventPosters/>;
      case "posters": return isAdmin ? <EventPosters/> : <ViewPosters/>;
      case "certificates": return isAdmin ? <CertificateManager/> : <Overview goTo={goTo}/>;
      case "campaigns": return <Campaigns/>;
      case "campaign-manager": return isAdmin ? <CampaignManager/> : <Overview goTo={goTo}/>;
      case "registrations": return isAdmin ? <RegistrationManager/> : <Overview goTo={goTo}/>;
      default:                 return <Overview goTo={goTo}/>;
    }
  };

  const allNav = [...NAV, ...(isAdmin ? ADMIN_NAV : [])];
  const currentLabel = allNav.find(n=>n.id===page)?.label || "Dashboard";

  return (
    <div style={{display:"flex",minHeight:"100vh",background:"#0a0908",fontFamily:"'Plus Jakarta Sans',system-ui,sans-serif"}}>
      <Toaster position="top-center"/>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Playfair+Display:wght@700;800&display=swap" rel="stylesheet"/>
      <style>{`
        .db-sidebar{position:fixed;top:0;left:0;width:240px;height:100vh;z-index:100;background:#111009;border-right:1px solid rgba(255,255,255,0.06);display:flex;flex-direction:column;}
        .db-main{margin-left:240px;flex:1;display:flex;flex-direction:column;min-height:100vh;min-width:0;}
        .db-hamburger{display:none!important;}
        .db-overlay{display:none;}
        @media(max-width:768px){
          .db-sidebar{transform:translateX(-100%);transition:transform 0.3s;}
          .db-sidebar.open{transform:translateX(0);}
          .db-main{margin-left:0;}
          .db-hamburger{display:flex!important;}
          .db-overlay{display:block;position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:99;backdrop-filter:blur(2px);}
        }
        .nav-btn:hover{background:rgba(255,255,255,0.06)!important;color:#fef3e2!important;}
        .logout-btn:hover{background:rgba(239,68,68,0.15)!important;}
        * { box-sizing:border-box; }
      `}</style>

      {/* Overlay mobile */}
      {menuOpen && <div className="db-overlay" onClick={()=>setMenuOpen(false)}/>}

      {/* Sidebar */}
      <div className={`db-sidebar${menuOpen?" open":""}`}>
        {/* Brand */}
        <div style={{padding:"16px",borderBottom:"1px solid rgba(255,255,255,0.06)",display:"flex",alignItems:"center",gap:10}}>
          <img src={trustLogo} alt="HTS" style={{width:36,height:36,objectFit:"contain",borderRadius:9,flexShrink:0}}/>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:"#fef3e2",lineHeight:1.2}}>Help Them Shine</div>
            <div style={{fontSize:10,color:"#52504d",fontWeight:600,textTransform:"uppercase",letterSpacing:"0.5px"}}>Member Portal</div>
          </div>
        </div>

        {/* User */}
<div style={{padding:"14px 16px",borderBottom:"1px solid rgba(255,255,255,0.06)",display:"flex",alignItems:"center",gap:10}}>
  {userProfile?.photoUrl ? (
    <img src={userProfile.photoUrl} alt={name}
      style={{width:38,height:38,borderRadius:"50%",objectFit:"cover",border:`1.5px solid ${rc.border}`,flexShrink:0}}/>
  ) : (
    <div style={{width:38,height:38,borderRadius:"50%",background:rc.bg,border:`1.5px solid ${rc.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,fontWeight:800,color:rc.color,flexShrink:0}}>
      {name.charAt(0).toUpperCase()}
    </div>
  )}
  <div style={{flex:1,minWidth:0}}>
    <div style={{fontSize:13,fontWeight:700,color:"#fef3e2",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</div>
    <div style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:50,letterSpacing:"0.5px",marginTop:3,background:rc.bg,color:rc.color,border:`1px solid ${rc.border}`}}>
      {(role==="admin"||role==="superadmin") && <Shield size={9}/>}
      {role.toUpperCase()}
    </div>
  </div>
</div>

        {/* Nav */}
        <div style={{flex:1,padding:"10px 8px",overflowY:"auto"}}>
          <div style={{fontSize:10,fontWeight:700,color:"#52504d",textTransform:"uppercase",letterSpacing:"1.5px",padding:"6px 8px",marginBottom:2}}>Menu</div>
          {NAV.map(({id,label,icon:Icon})=>(
            <button key={id} onClick={()=>goTo(id)} className="nav-btn" style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 10px",borderRadius:9,border:"none",cursor:"pointer",fontSize:13,transition:"all 0.15s",marginBottom:1,fontFamily:"inherit",background:page===id?"rgba(245,158,11,0.12)":"transparent",color:page===id?"#f59e0b":"#a8a29e",fontWeight:page===id?700:500}}>
              <Icon size={16} style={{flexShrink:0}}/><span style={{flex:1,textAlign:"left"}}>{label}</span>
              {page===id && <ChevronRight size={13}/>}
            </button>
          ))}

          {isAdmin && (
            <>
              <div style={{fontSize:10,fontWeight:700,color:"#52504d",textTransform:"uppercase",letterSpacing:"1.5px",padding:"6px 8px",marginTop:14,marginBottom:2}}>Admin</div>
              {ADMIN_NAV.map(({id,label,icon:Icon})=>(
                <button key={id} onClick={()=>goTo(id)} className="nav-btn" style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"9px 10px",borderRadius:9,border:"none",cursor:"pointer",fontSize:13,transition:"all 0.15s",marginBottom:1,fontFamily:"inherit",background:page===id?"rgba(59,130,246,0.12)":"transparent",color:page===id?"#3b82f6":"#a8a29e",fontWeight:page===id?700:500}}>
                  <Icon size={16} style={{flexShrink:0}}/><span style={{flex:1,textAlign:"left"}}>{label}</span>
                  {page===id && <ChevronRight size={13}/>}
                </button>
              ))}
            </>
          )}
        </div>

        {/* Logout */}
        <div style={{padding:12,borderTop:"1px solid rgba(255,255,255,0.06)"}}>
          <button onClick={handleLogout} className="logout-btn" style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"9px",background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:9,color:"#ef4444",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s"}}>
            <LogOut size={15}/> Sign Out
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="db-main">
        {/* Topbar */}
        <div style={{position:"sticky",top:0,zIndex:50,background:"rgba(17,16,9,0.92)",backdropFilter:"blur(16px)",borderBottom:"1px solid rgba(255,255,255,0.06)",padding:"0 20px",height:60,display:"flex",alignItems:"center",gap:14}}>
          <button className="db-hamburger" onClick={()=>setMenuOpen(o=>!o)} style={{background:"rgba(255,255,255,0.06)",border:"none",borderRadius:8,padding:7,color:"#a8a29e",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            {menuOpen ? <X size={20}/> : <Menu size={20}/>}
          </button>
          <div style={{flex:1,fontSize:15,fontWeight:700,color:"#fef3e2"}}>{currentLabel}</div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{position:"relative",width:34,height:34,background:"rgba(255,255,255,0.06)",borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
              <NotificationBell/>
              <div style={{position:"absolute",top:7,right:7,width:7,height:7,background:"#ef4444",borderRadius:"50%",border:"1.5px solid #111009"}}/>
            </div>
            {userProfile?.photoUrl ? (
  <img src={userProfile.photoUrl} alt={name}
    style={{width:34,height:34,borderRadius:"50%",objectFit:"cover",border:`1.5px solid ${rc.border}`,cursor:"pointer"}}/>
) : (
  <div style={{width:34,height:34,borderRadius:"50%",background:rc.bg,border:`1.5px solid ${rc.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:rc.color,cursor:"pointer"}}>
    {name.charAt(0).toUpperCase()}
  </div>
)}
          </div>
        </div>

        {/* Content */}
        <div style={{flex:1,padding:24,overflowY:"auto"}}>
          {renderPage()}
        </div>
      </div>
    </div>
  );
}